// ** Modules
import React from 'react';

// ** Theme
import themeConfig from '@/theme/themeConfig';

// ** Types
import type { Settings, SettingsContextValue } from '@/types/contextTypes';

type SettingsProviderProps = {
	children: React.ReactNode;
	pageSettings?: Settings;
};

// Initial Settings
const initialSettings: Settings = {
	...themeConfig,
	appBar: themeConfig.layout === 'default' && themeConfig.appBar === 'hidden' ? 'fixed' : themeConfig.appBar,
	lastLayout: themeConfig.layout,
	themeColor: 'primary'
};

// const staticSettings = {
// 	appBar: initialSettings.appBar,
// 	footer: initialSettings.footer,
// 	layout: initialSettings.layout,
// 	navHidden: initialSettings.navHidden,
// 	lastLayout: initialSettings.lastLayout,
// 	toastPosition: initialSettings.toastPosition
// };

// Settings not allowed to get stored
const PersistenceBlacklist: (keyof Settings)[] = [
	'appBar',
	'footer',
	'layout',
	'navHidden',
	'lastLayout',
	'toastPosition'
];

const restoreSettings = (): Settings => {
	try {
		const storedData: string | null = window.localStorage.getItem('tischlerSettings');

		if (storedData) {
			return { ...initialSettings, ...JSON.parse(storedData) };
		}
	} catch (err) {
		console.error('Settings restoration failed', err);
	}

	return initialSettings;
};

// Set settings in localStorage
const storeSettings = (settings: Settings) => {
	const toStore = { ...settings };
	PersistenceBlacklist.forEach((key) => {
		delete toStore[key as keyof typeof toStore];
	});
	window.localStorage.setItem('tischlerSettings', JSON.stringify(toStore));
};

// Create Context
export const SettingsContext = React.createContext<SettingsContextValue>({
	saveSettings: () => null,
	settings: initialSettings
});

export const SettingsProvider = ({ children, pageSettings }: SettingsProviderProps) => {
	// State
	const [settings, setSettings] = React.useState<Settings>({
		...initialSettings,
		...(pageSettings || {})
	});

	// Handle the LocalStorage restoration
	React.useEffect(() => {
		const restored = restoreSettings();

		setSettings((prev) => ({
			...restored, // Load saved user preferences
			...prev, // Keep the current state (which includes pageSettings)
			...pageSettings // Re-apply CMS settings as the ultimate winner
		}));
	}, [pageSettings]);

	// Keep the secondary effect for dynamic changes
	React.useEffect(() => {
		if (pageSettings) {
			setSettings((prev) => ({ ...prev, ...pageSettings }));
		}
	}, [pageSettings]);

	const saveSettings = (updatedSettings: Settings) => {
		storeSettings(updatedSettings);
		setSettings(updatedSettings);
	};

	return <SettingsContext.Provider value={{ settings, saveSettings }}>{children}</SettingsContext.Provider>;
};

export const SettingsConsumer = SettingsContext.Consumer;
